import { useMemo } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";

import { useAuth } from "../../../../providers/AuthProvider";

import { PageShell } from "../../../shared/layouts/PageShell";
import { SectionWithHeader } from "../../../shared/ui/SectionWithHeader";

import {
  ArrowRight,
  CheckCircle,
  Clock,
  Gift,
  MagnifyingGlass,
  Storefront,
  SquaresFourIcon,
  UserCircle,
  ChatCircleDots,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../../shared/ui/icons";
import { Avatar } from "../../../shared/ui/Avatar";
import { SurfaceCard } from "../../../shared/ui/surface/SurfaceCard";
import { useResponsiveBreakpoint } from "../../../../hooks/useResponsiveBreakpoint";
import { InfoBanner } from "../../../../components/InfoBanner";

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const heroLayoutClasses =
  "gap-4 sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center";
const heroBrandClasses = "flex items-center gap-6";
const heroTextClasses = "flex flex-col gap-1";
const heroTitleClasses = "text-3xl font-bold text-slate-900 dark:text-slate-100 md:text-4xl";
const heroTaglineClasses = "text-base font-semibold text-slate-700 dark:text-slate-200";
const heroLogoClasses =
  "h-20 w-20 rounded-3xl bg-slate-100 p-3 shadow-lg ring-1 ring-white dark:bg-slate-800 dark:ring-slate-700";
const userCardClasses =
  "flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-900";
const userCardLinkClasses =
  "transition hover:border-sky-200 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:hover:border-slate-700";
const userIdentityNameClasses =
  "max-w-[200px] truncate text-sm font-semibold text-slate-900 dark:text-slate-100";
const userIdentityDetailClasses = "text-xs text-slate-500 dark:text-slate-400";
const actionsGridClasses =
  "grid gap-4 md:grid-cols-2 xl:grid-cols-3";
const futureGridClasses = "grid gap-4 md:grid-cols-2";
const cardBaseClasses = "flex flex-col gap-4";
const cardTitleClasses = "text-lg font-semibold text-slate-900 dark:text-slate-100";
const ctaButtonClasses =
  "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";
const disabledCtaClasses =
  "inline-flex items-center gap-2 rounded-md border border-dashed border-slate-300 bg-white/40 px-4 py-2 text-sm font-semibold text-slate-400 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-500";
const statusListClasses =
  "grid w-full gap-3 text-sm text-slate-700 sm:grid-cols-2 dark:text-slate-200";
const statusItemClasses =
  "flex items-center gap-3 rounded-xl bg-white/80 px-3.5 py-2.5 shadow-sm ring-1 ring-slate-100 dark:bg-slate-900/60 dark:ring-slate-700";
const statusLabelClasses =
  "flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400";
const statusValueClasses = "text-sm font-semibold";

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDesktopUp } = useResponsiveBreakpoint();

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [];
  }, []);

  const dashboardActions = useMemo(() => {
    if (!user) {
      return [];
    }
    const actions = [
      {
        title: t("dashboard.manage_workspaces.title"),
        description: t("dashboard.manage_workspaces.description"),
        cta: {
          label: t("dashboard.manage_workspaces.action"),
          to: "/workspaces",
          icon: <SquaresFourIcon {...defaultIconProps} aria-hidden />,
        },
      },
      {
        title: t("dashboard.billing_overview.title", "Wallet & Billing"),
        description: t(
          "dashboard.billing_overview.description",
          "Review your credit balance and open billing to purchase more."
        ),
        cta: {
          label: t("dashboard.billing_overview.action", "Open billing"),
          to: "/billing",
          icon: <SquaresFourIcon {...defaultIconProps} aria-hidden />,
        },
      },
      {
        title: t("dashboard.support.title", "Customer service"),
        description: t(
          "dashboard.support.description",
          "Chat with our team when you need help with surveys or billing."
        ),
        cta: {
          label: t("dashboard.support.action", "Open support"),
          to: "/support",
          icon: <ChatCircleDots {...defaultIconProps} aria-hidden />,
        },
      },
      {
        title: t("dashboard.profile_settings.title"),
        description: t("dashboard.profile_settings.description"),
        cta: {
          label: t("dashboard.profile_settings.action"),
          to: "/settings/profile",
          icon: <UserCircle {...defaultIconProps} aria-hidden />,
        },
      },
    ];

    if (user.system_role === "admin") {
      actions.push({
        title: t("dashboard.admin_control.title"),
        description: t("dashboard.admin_control.description"),
        cta: {
          label: t("dashboard.admin_control.action"),
          to: "/admin",
          icon: <SquaresFourIcon {...defaultIconProps} aria-hidden />, 
        },
      });
    }

    return actions;
  }, [user, t]);

  const futureActions = useMemo(
    () => [
      {
        title: t("dashboard.sell_lots.title"),
        description: t("dashboard.sell_lots.description"),
        icon: <Storefront {...defaultIconProps} aria-hidden />,
      },
      {
        title: t("dashboard.browse_market.title"),
        description: t("dashboard.browse_market.description"),
        icon: <MagnifyingGlass {...defaultIconProps} aria-hidden />,
      },
    ],
    [t],
  );

  if (!user) {
    return null;
  }

  const hasVerifiedEmail = Boolean(user.email_verified);
  const hasVerifiedPhone = Boolean(user.phone_verified);
  const hasSignupBonus = hasVerifiedEmail && hasVerifiedPhone;

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />

      <header className={heroLayoutClasses}>
        <div className={heroBrandClasses}>
          <img
            src="/lotlocator-icon.png"
            alt="LotLocator logo"
            className={heroLogoClasses}
            width={80}
            height={80}
            loading="eager"
          />
          <div className={heroTextClasses}>
            <h1 className={heroTitleClasses}>LotLocator.ph</h1>
            <p className={heroTaglineClasses}>{t("dashboard.tagline")}</p>
          </div>
        </div>

        {isDesktopUp ? (
          <Link
            to="/settings/profile"
            className={`${userCardClasses} ${userCardLinkClasses}`}
          >
            <Avatar
              name={user.name}
              email={user.email}
              photoUrl={user.profile_photo?.download_url ?? null}
              fallbackUrl={user.profile_photo?.remote_url ?? null}
              size={40}
              className="shadow-md ring-2 ring-white dark:ring-slate-900"
            />
            <div className="flex flex-col text-right leading-tight">
              <strong className={userIdentityNameClasses}>
                {user.name ?? t("dashboard.unnamed_user")}
              </strong>
              <small className={userIdentityDetailClasses}>{user.email}</small>
            </div>
          </Link>
        ) : null}
      </header>

      {hasSignupBonus ? null : (
        <InfoBanner
          tone="info"
          prominent
          iconSize="lg"
          icon={<Gift {...defaultIconProps} size={28} aria-hidden />}
          title={t("dashboard.signup_bonus.locked_title")}
          description={
            <div className="space-y-3">
              <p>
                Verify your account to unlock the full LotLocator.ph toolkit. We&apos;ll drop 20 credits
                into your wallet so you can map two properties right away as a welcome boost.
              </p>
              <div className={statusListClasses}>
                {[
                {
                  key: "email",
                  label: t("forms.email.label"),
                  value: hasVerifiedEmail
                    ? t("dashboard.signup_bonus.status_verified")
                    : t("dashboard.signup_bonus.status_pending"),
                  to: "/settings/profile#profile-email",
                  icon: hasVerifiedEmail ? (
                    <CheckCircle
                      {...defaultIconProps}
                      weight="fill"
                      className="text-emerald-600"
                      aria-hidden
                    />
                  ) : (
                    <Clock
                      {...defaultIconProps}
                      className="text-amber-600"
                      aria-hidden
                    />
                  ),
                  toneClass: hasVerifiedEmail
                    ? "text-emerald-700 dark:text-emerald-200"
                    : "text-slate-600 dark:text-slate-400",
                },
                {
                  key: "phone",
                  label: t("forms.phone.label"),
                  value: hasVerifiedPhone
                    ? t("dashboard.signup_bonus.status_verified")
                    : t("dashboard.signup_bonus.status_pending"),
                  to: "/settings/profile#profile-phone",
                  icon: hasVerifiedPhone ? (
                    <CheckCircle
                      {...defaultIconProps}
                      weight="fill"
                      className="text-emerald-600"
                      aria-hidden
                    />
                  ) : (
                    <Clock
                      {...defaultIconProps}
                      className="text-amber-600"
                      aria-hidden
                    />
                  ),
                  toneClass: hasVerifiedPhone
                    ? "text-emerald-700 dark:text-emerald-200"
                    : "text-amber-700 dark:text-amber-200",
                },
              ].map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`${statusItemClasses} hover:-translate-y-0.5 hover:ring-amber-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500 dark:hover:ring-amber-700`}
                  aria-label={item.label}
                >
                  {item.icon}
                  <div className="flex flex-col">
                    <span className={statusLabelClasses}>{item.label}</span>
                    <span className={`${statusValueClasses} ${item.toneClass}`}>
                      {item.value}
                    </span>
                  </div>
                </Link>
              ))}
              </div>
            </div>
          }
        />
      )}

      <SectionWithHeader
        className="flex flex-col gap-6"
        title={t("dashboard.quick_actions.title")}
        description={t("dashboard.quick_actions.description")}
      >
        <div className={actionsGridClasses}>
          {dashboardActions.map((action) => (
            <QuickActionCard key={action.title} action={action} />
          ))}
        </div>
      </SectionWithHeader>

      <SectionWithHeader
        className="flex flex-col gap-6"
        title={t("dashboard.future_actions.title")}
        description={t("dashboard.future_actions.description")}
      >
        <div className={futureGridClasses}>
          {futureActions.map((action) => (
            <SurfaceCard
              key={action.title}
              className={`${cardBaseClasses} opacity-80`}
              tone="muted"
            >
              <SurfaceCard.Body gap="tight" textTone="muted">
                <h4 className={cardTitleClasses}>{action.title}</h4>
                <p>{action.description}</p>
              </SurfaceCard.Body>
              <SurfaceCard.Footer justify="end">
                <span className={disabledCtaClasses} aria-disabled="true">
                  {action.icon}
                  {t("dashboard.future_actions.action")}
                </span>
              </SurfaceCard.Footer>
            </SurfaceCard>
          ))}
        </div>
      </SectionWithHeader>
    </PageShell>
  );
}

interface QuickAction {
  title: string;
  description: string;
  cta: {
    label: string;
    to: string;
    icon: ReactNode;
  };
}

function QuickActionCard({ action }: { action: QuickAction }) {
  return (
    <SurfaceCard
      className={cardBaseClasses}
      tone="interactive"
      link={{
        to: action.cta.to,
        label: `${action.title} — ${action.cta.label}`,
      }}
    >
      <SurfaceCard.Body gap="tight" textTone="muted">
        <h4 className={cardTitleClasses}>{action.title}</h4>
        <p>{action.description}</p>
      </SurfaceCard.Body>
      <SurfaceCard.Footer justify="end">
        <span className={ctaButtonClasses} aria-hidden="true">
          {action.cta.icon}
          {action.cta.label}
          <ArrowRight {...defaultIconProps} aria-hidden />
        </span>
      </SurfaceCard.Footer>
    </SurfaceCard>
  );
}
